/*
 * cw-commerce.js — CutWave / Shrine PRO 1.7.0
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 *
 * This theme only ships `secondary.js`, which defines 31 custom elements — all
 * of them presentational (sliders, menu drawers, timelines, tabs). Shrine PRO's
 * PRIMARY bundle, which defines the commerce elements, is not present in
 * assets/. The Liquid still emits those tags, so at runtime they are inert
 * HTMLUnknownElements and every JS-driven commerce interaction silently
 * degrades to a full page navigation.
 *
 * This file re-implements the missing elements against the markup contracts
 * that Shrine's own Liquid produces:
 *
 *   <product-form>        snippets/buy-buttons.liquid
 *   <variant-selects>     snippets/product-variant-picker.liquid
 *   <quantity-input>      blocks/product_quantity-selector.liquid, cart items
 *   <quantity-breaks>     snippets/quantity-breaks.liquid
 *   <cart-drawer>         sections/cart-drawer.liquid
 *   <cart-drawer-items>   blocks/cart-drawer_items.liquid
 *   <cart-items>          sections/main-cart-items.liquid
 *   <cart-remove-button>  both of the above
 *   <sticky-atc>          blocks/product_sticky-atc.liquid
 *
 * Every definition is guarded with `customElements.get()`. If the real Shrine
 * bundle is ever restored, this file yields to it without conflict.
 *
 * No localStorage / sessionStorage is used anywhere in here.
 */
(function () {
  'use strict';

  var routes = window.routes || {};
  var ROOT = routes.root_url || '/';
  if (ROOT.charAt(ROOT.length - 1) !== '/') ROOT += '/';

  var CART_ADD = (routes.cart_add_url || ROOT + 'cart/add') + '.js';
  var CART_CHANGE = (routes.cart_change_url || ROOT + 'cart/change') + '.js';
  var CART_URL = routes.cart_url || ROOT + 'cart';

  /* Sections re-rendered by the Cart AJAX API after every mutation.
     `cart-drawer` is rendered by layout/theme.liquid; `header` carries
     #cart-icon-bubble. Requesting the header section keeps the bubble's
     `cart-hidden` tag logic in Liquid instead of duplicating it here. */
  var CART_SECTIONS = 'cart-drawer,header';

  var strings = window.cartStrings || {};
  var GENERIC_ERROR = strings.error || 'Something went wrong. Please try again.';

  /* ----------------------------------------------------------------------- */
  /* helpers                                                                 */
  /* ----------------------------------------------------------------------- */

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* Replace target's children with the matching node's children from `html`.
     Returns true when the swap happened. */
  function swapInner(html, selector, target) {
    if (!html || !target) return false;
    var source = parseHTML(html).querySelector(selector);
    if (!source) return false;
    target.innerHTML = source.innerHTML;
    return true;
  }

  /* Apply the `sections` payload returned by the Cart AJAX API. */
  function applyCartSections(sections) {
    if (!sections) return;

    var drawer = document.querySelector('cart-drawer');
    if (sections['cart-drawer'] && drawer) {
      var wasOpen = drawer.classList.contains('active');
      swapInner(sections['cart-drawer'], 'cart-drawer', drawer);
      // `is-empty` lives on the element itself, so it is not covered by the
      // inner swap — recompute it from the freshly rendered markup.
      var fresh = parseHTML(sections['cart-drawer']).querySelector('cart-drawer');
      if (fresh) drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      if (wasOpen) drawer.classList.add('active');
    }

    var bubble = document.getElementById('cart-icon-bubble');
    if (sections.header && bubble) {
      swapInner(sections.header, '#cart-icon-bubble', bubble);
    }
  }

  /* Cart AJAX API accepts `sections` + `sections_url` alongside the payload. */
  function withSections(formData) {
    formData.append('sections', CART_SECTIONS);
    formData.append('sections_url', window.location.pathname);
    return formData;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(readResponse);
  }

  function postForm(url, formData) {
    return fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: formData
    }).then(readResponse);
  }

  function readResponse(response) {
    return response.json().then(function (data) {
      if (!response.ok) throw data;
      return data;
    });
  }

  function errorMessage(err) {
    if (!err) return GENERIC_ERROR;
    return err.description || err.message || GENERIC_ERROR;
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* Shopify money formatting, reduced to the four format tokens the Liquid
     `money_format` setting can actually contain. */
  function formatMoney(cents, format) {
    var value = Number(cents) || 0;
    var fmt = format || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimal + parts[1] : '';
      return whole + fraction;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, token) {
      switch (token) {
        case 'amount':
          return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  /* ----------------------------------------------------------------------- */
  /* <cart-drawer>                                                           */
  /* ----------------------------------------------------------------------- */
  /* sections/cart-drawer.liquid calls `this.closest('cart-drawer').close()`
     inline, so open()/close() are part of the public contract. */

  if (!customElements.get('cart-drawer')) {
    customElements.define(
      'cart-drawer',
      class CartDrawer extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;

          this.addEventListener('click', function (event) {
            if (event.target.closest('.drawer__close')) {
              event.preventDefault();
              this.close();
              return;
            }
            if (event.target.id === 'CartDrawer-Overlay' ||
                event.target.classList.contains('cart-drawer__overlay')) {
              this.close();
            }
          });

          this._onKeyup = function (event) {
            if (event.key === 'Escape' && this.classList.contains('active')) this.close();
          }.bind(this);
          document.addEventListener('keyup', this._onKeyup);
        }

        disconnectedCallback() {
          if (this._onKeyup) document.removeEventListener('keyup', this._onKeyup);
        }

        open() {
          this.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          var panel = this.querySelector('.drawer__inner');
          if (panel) {
            // Wait for the transition to start before moving focus, otherwise
            // Safari scrolls the page to the offscreen panel.
            requestAnimationFrame(function () { panel.focus(); });
          }
        }

        close() {
          this.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
        }
      }
    );
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    drawer.open();
    return true;
  }

  /* ----------------------------------------------------------------------- */
  /* <product-form>                                                          */
  /* ----------------------------------------------------------------------- */

  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;
          /* Listening on the element rather than the <form> avoids a lifecycle
             trap: the constructor/connectedCallback runs at the OPENING tag
             during parsing, when children do not exist yet. It also lets the
             duo-pack guard in buy-buttons.liquid — a capture-phase listener on
             the form that calls stopImmediatePropagation() — block us cleanly
             when the two cap selects are empty. */
          this.addEventListener('submit', this.onSubmit.bind(this));
        }

        get form() {
          return this.querySelector('form');
        }

        /* layout/theme.liquid calls productForm.handleSubmit(e) from the
           sticky/section ATC buttons. Route through requestSubmit so the duo
           guard and native validation still run. */
        handleSubmit(event) {
          if (event && typeof event.preventDefault === 'function') event.preventDefault();
          var form = this.form;
          if (!form) return;
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            this.onSubmit({ target: form, preventDefault: function () {} });
          }
        }

        onSubmit(event) {
          if (event.defaultPrevented) return;
          if (typeof event.preventDefault === 'function') event.preventDefault();

          var form = event.target && event.target.tagName === 'FORM' ? event.target : this.form;
          if (!form) return;
          if (this._submitting) return;
          this._submitting = true;

          this.hideError();
          this.setLoading(true);

          var skipCart = !!form.querySelector('input[name="id"][data-skip-cart="true"]');

          postForm(CART_ADD, withSections(new FormData(form)))
            .then(function (data) {
              if (skipCart) {
                window.location.href = ROOT + 'checkout';
                return;
              }
              applyCartSections(data.sections);
              if (!openCartDrawer()) window.location.href = CART_URL;
              document.dispatchEvent(
                new CustomEvent('cw:cart:updated', { detail: { source: 'add' } })
              );
            })
            .catch(
              function (err) {
                this.showError(errorMessage(err));
              }.bind(this)
            )
            .finally(
              function () {
                this._submitting = false;
                this.setLoading(false);
              }.bind(this)
            );
        }

        setLoading(isLoading) {
          var buttons = this.querySelectorAll('[type="submit"][name="add"], .main-product-atc');
          Array.prototype.forEach.call(buttons, function (button) {
            button.classList.toggle('loading', isLoading);
            if (isLoading) {
              button.setAttribute('aria-disabled', 'true');
            } else {
              button.removeAttribute('aria-disabled');
            }
            var spinner = button.querySelector('.loading-overlay__spinner');
            if (spinner) spinner.classList.toggle('hidden', !isLoading);
          });
        }

        showError(message) {
          var wrapper = this.querySelector('.product-form__error-message-wrapper');
          var target = wrapper && wrapper.querySelector('.product-form__error-message');
          if (wrapper && target) {
            target.textContent = message;
            wrapper.hidden = false;
          } else {
            window.alert(message);
          }
        }

        hideError() {
          var wrapper = this.querySelector('.product-form__error-message-wrapper');
          if (wrapper) wrapper.hidden = true;
        }
      }
    );
  }

  /* ----------------------------------------------------------------------- */
  /* <variant-selects>                                                       */
  /* ----------------------------------------------------------------------- */
  /* Markup contract (snippets/product-variant-picker.liquid):
       - dropdowns:  <select name="options[Name]">
       - pills / swatches / breaks:  <input type="radio" name="OptionName">
       - trailing <script type="application/json"> holding product.variants
     Option order follows product.options_with_values, i.e. DOM order. */

  if (!customElements.get('variant-selects')) {
    customElements.define(
      'variant-selects',
      class VariantSelects extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;
          this.addEventListener('change', this.onChange.bind(this));
        }

        get variants() {
          if (this._variants) return this._variants;
          var json = this.querySelector('script[type="application/json"]');
          try {
            this._variants = json ? JSON.parse(json.textContent) : [];
          } catch (e) {
            this._variants = [];
          }
          return this._variants;
        }

        get sectionId() {
          return this.dataset.section;
        }

        /* One selected value per option group, in DOM order. */
        selectedOptions() {
          var values = [];
          var groups = this.querySelectorAll('.product-form__input');
          Array.prototype.forEach.call(groups, function (group) {
            var select = group.querySelector('select');
            if (select) {
              values.push(select.value);
              return;
            }
            var checked = group.querySelector('input[type="radio"]:checked');
            if (checked) values.push(checked.value);
          });
          return values;
        }

        onChange() {
          var selected = this.selectedOptions();
          var match = this.variants.find(function (variant) {
            return selected.every(function (value, index) {
              return variant.options[index] === value;
            });
          });

          this.updateFormId(match);
          this.updateURL(match);

          if (!match) {
            this.setUnavailable();
            return;
          }
          this.renderSection(match);
        }

        updateFormId(variant) {
          var form = document.getElementById('product-form-' + this.sectionId);
          var inputs = document.querySelectorAll(
            'input[name="id"].product-variant-id, #product-form-' + this.sectionId + ' input[name="id"]'
          );
          Array.prototype.forEach.call(inputs, function (input) {
            if (form && input.form && input.form !== form) return;
            input.value = variant ? variant.id : '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }

        updateURL(variant) {
          if (!variant || this.dataset.updateUrl === 'false' || !this.dataset.url) return;
          window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
        }

        setUnavailable() {
          var button = document.getElementById('ProductSubmitButton-' + this.sectionId);
          if (!button) return;
          button.setAttribute('disabled', 'disabled');
          var label = button.querySelector('.main-atc__label .button__label, .button__label');
          if (label && window.variantStrings) label.textContent = window.variantStrings.unavailable;
        }

        /* Section Rendering API keeps price / badges / inventory / SKU in
           Liquid rather than reimplementing money and discount logic here. */
        renderSection(variant) {
          var url = this.dataset.url + '?variant=' + variant.id + '&section_id=' + this.sectionId;
          fetch(url)
            .then(function (response) { return response.text(); })
            .then(
              function (html) {
                var doc = parseHTML(html);
                var self = this;
                ['#price-', '#Sku-', '#Inventory-'].forEach(function (prefix) {
                  var id = prefix + self.sectionId;
                  var source = doc.querySelector(id);
                  var target = document.querySelector(id);
                  if (source && target) {
                    target.innerHTML = source.innerHTML;
                    target.classList.toggle('hidden', source.classList.contains('hidden'));
                  }
                });

                var newButton = doc.getElementById('ProductSubmitButton-' + this.sectionId);
                var button = document.getElementById('ProductSubmitButton-' + this.sectionId);
                if (newButton && button) {
                  button.innerHTML = newButton.innerHTML;
                  if (newButton.hasAttribute('disabled')) {
                    button.setAttribute('disabled', 'disabled');
                  } else {
                    button.removeAttribute('disabled');
                  }
                }

                document.dispatchEvent(
                  new CustomEvent('cw:variant:changed', { detail: { variant: variant } })
                );
              }.bind(this)
            )
            .catch(function () {
              /* Leave the rendered price in place rather than blanking it. */
            });
        }
      }
    );
  }

  /* ----------------------------------------------------------------------- */
  /* <quantity-input>                                                        */
  /* ----------------------------------------------------------------------- */
  /* Used in two contexts. On a PDP it is a plain stepper. In cart markup the
     input is name="updates[]" with data-index, and the change must be pushed
     to /cart/change.js — that is handled by the cart item elements below, so
     here we only need to keep the value legal and fire `change`. */

  if (!customElements.get('quantity-input')) {
    customElements.define(
      'quantity-input',
      class QuantityInput extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;
          this.addEventListener('click', this.onClick.bind(this));
        }

        get input() {
          return this.querySelector('input.quantity__input, input[type="number"]');
        }

        onClick(event) {
          var button = event.target.closest('button[name="plus"], button[name="minus"]');
          if (!button) return;
          event.preventDefault();

          var input = this.input;
          if (!input) return;

          var step = Number(input.step) || 1;
          var min = input.min === '' ? 0 : Number(input.min);
          var max = input.max === '' ? Infinity : Number(input.max);
          var current = Number(input.value) || 0;
          var next = button.name === 'plus' ? current + step : current - step;

          next = Math.min(Math.max(next, min), max);
          if (next === current) return;

          input.value = String(next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    );
  }

  /* ----------------------------------------------------------------------- */
  /* cart line items: <cart-drawer-items>, <cart-items>, <cart-remove-button> */
  /* ----------------------------------------------------------------------- */

  function changeLine(line, quantity, rootElement) {
    if (rootElement) rootElement.classList.add('cart--loading');

    return postJSON(CART_CHANGE, {
      line: line,
      quantity: quantity,
      sections: CART_SECTIONS.split(','),
      sections_url: window.location.pathname
    })
      .then(function (data) {
        applyCartSections(data.sections);
        document.dispatchEvent(
          new CustomEvent('cw:cart:updated', { detail: { source: 'change' } })
        );
        /* The /cart page is not in CART_SECTIONS — reload it so totals, the
           line list and any discount rows stay consistent. */
        if (!document.querySelector('cart-drawer.active') && document.querySelector('cart-items')) {
          window.location.reload();
        }
      })
      .catch(function (err) {
        window.alert(errorMessage(err));
      })
      .finally(function () {
        if (rootElement) rootElement.classList.remove('cart--loading');
      });
  }

  function defineCartItems(tagName) {
    if (customElements.get(tagName)) return;
    customElements.define(
      tagName,
      class CartLineItems extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;

          this.addEventListener('change', function (event) {
            var input = event.target.closest('input[name="updates[]"]');
            if (!input) return;
            var line = input.dataset.index;
            if (!line) return;
            changeLine(Number(line), Number(input.value), this);
          });

          this.addEventListener('click', function (event) {
            var remove = event.target.closest('cart-remove-button');
            if (!remove) return;
            event.preventDefault();
            var line = remove.dataset.index;
            if (!line) return;
            changeLine(Number(line), 0, this);
          });
        }
      }
    );
  }

  defineCartItems('cart-drawer-items');
  defineCartItems('cart-items');

  /* Defined so the tag is a known element (styling/`closest` stay predictable);
     the click is handled by the delegating parent above. */
  if (!customElements.get('cart-remove-button')) {
    customElements.define('cart-remove-button', class extends HTMLElement {});
  }

  /* ----------------------------------------------------------------------- */
  /* <quantity-breaks>                                                       */
  /* ----------------------------------------------------------------------- */
  /* The radios are `name="quantity" form="product-form-<section>"`, so the
     correct quantity reaches /cart/add without any JS at all — selection state
     is pure CSS (`input:checked + .quantity-break`). What JS adds is keeping
     the headline PDP price in step with the selected tier. */

  if (!customElements.get('quantity-breaks')) {
    customElements.define(
      'quantity-breaks',
      class QuantityBreaks extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;
          this.addEventListener('change', this.onChange.bind(this));
          this.onChange();
        }

        onChange() {
          if (this.dataset.updatePdpPrices !== 'true') return;

          var checked = this.querySelector('input[name="quantity"]:checked');
          if (!checked) return;
          var label = this.querySelector('label[data-input="' + checked.dataset.input + '"]');
          if (!label) return;

          var base = Number(this.dataset.basePrice) || 0;
          var quantity = Number(label.dataset.quantity) || 1;
          var percentageLeft = parseFloat(label.dataset.percentageLeft);
          if (isNaN(percentageLeft)) percentageLeft = 1;
          var fixed = Number(label.dataset.fixedDiscount) || 0;

          // Mirrors the Liquid at snippets/quantity-breaks.liquid:248.
          var total = Math.round(base * quantity * percentageLeft) - fixed;
          if (total < 0) total = 0;

          var priceEl = document.querySelector(
            '#price-' + this.dataset.section + ' .price-item--regular'
          );
          if (!priceEl) return;

          if (this._originalPrice === undefined) this._originalPrice = priceEl.innerHTML;
          priceEl.innerHTML =
            quantity === 1
              ? this._originalPrice
              : formatMoney(total, this.dataset.moneyFormat || '€{{amount_with_comma_separator}}');
        }
      }
    );
  }

  /* ----------------------------------------------------------------------- */
  /* <sticky-atc>                                                            */
  /* ----------------------------------------------------------------------- */
  /* base.css hides it with `.sticky-atc[data-after-scroll="true"] {
     transform: translateY(100%) }`, so revealing it means flipping the
     attribute value rather than adding a class. */

  if (!customElements.get('sticky-atc')) {
    customElements.define(
      'sticky-atc',
      class StickyAtc extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;

          if (this.dataset.scrollBtn === 'true') this.bindScrollButton();
          if (this.dataset.afterScroll !== 'true') return;

          onReady(
            function () {
              /* Reveal once the main buy button has scrolled out of view. */
              var anchor =
                document.getElementById('ProductSubmitButton-' + this.dataset.section) ||
                document.getElementById('ProductForm-' + this.dataset.section);

              if (!anchor || typeof IntersectionObserver === 'undefined') {
                this.dataset.afterScroll = 'false';
                return;
              }

              var observer = new IntersectionObserver(
                function (entries) {
                  this.dataset.afterScroll = entries[0].isIntersecting ? 'true' : 'false';
                }.bind(this),
                { rootMargin: '0px 0px -80px 0px' }
              );
              observer.observe(anchor);
            }.bind(this)
          );
        }

        bindScrollButton() {
          var destination = this.dataset.scrollDestination;
          this.addEventListener('click', function (event) {
            var button = event.target.closest('.sticky-atc__scroll-btn, .sticky-atc__add_button');
            if (!button) return;
            event.preventDefault();
            var target =
              document.getElementById(destination) ||
              document.querySelector('[data-scroll-target="' + destination + '"]') ||
              document.querySelector('.product__info-container');
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      }
    );
  }

  /* ----------------------------------------------------------------------- */
  /* section / sticky ATC buttons -> main product form                        */
  /* ----------------------------------------------------------------------- */
  /* blocks/product_sticky-atc.liquid renders `<button id="SectionAtcBtn-…"
     type="button">`, which submits nothing on its own. Delegated so it works
     regardless of upgrade order, and skipped when the sticky bar is configured
     as a scroll-to button rather than an add-to-cart. */

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[id^="SectionAtcBtn-"]');
    if (!button) return;

    var sticky = button.closest('sticky-atc');
    if (sticky && sticky.dataset.scrollBtn === 'true') return;

    var form =
      document.querySelector('product-form[data-main="true"]') ||
      document.querySelector('product-form');
    if (!form || typeof form.handleSubmit !== 'function') return;

    /* Shrine's secondary buy button carries data-skip-cart-button and is meant
       to go straight to checkout. It shares the SectionAtcBtn- id prefix with
       the sticky bar, so distinguish them by the attribute, not the id. */
    if (button.dataset.skipCartButton === 'true') {
      var idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.dataset.skipCart = 'true';
    }

    form.handleSubmit(event);
  });

  /* ----------------------------------------------------------------------- */
  /* header cart link -> open the drawer instead of navigating                */
  /* ----------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.header__icon--cart');
    if (!trigger) return;
    if (!document.querySelector('cart-drawer')) return;
    if (document.querySelector('cart-drawer.is-empty')) return; // let it navigate
    event.preventDefault();
    openCartDrawer();
  });
})();
